import React, { memo, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import { useRouter } from 'next/router';
import { Spinner } from 'components';
import { useNotification } from 'hooks/useNotification';
import { useEvent } from 'hooks/useEvent';
import {
  EventAttributes,
  EventRequestStep1,
  SocietyEventRequestStep2,
  CompanyEventRequestStep3,
} from 'lib/models/event';
import { useCampaigns } from 'lib/contexts/campaigns-context';
import { PAGE_ROUTES } from 'lib/page-routes';
import { API_ROUTES } from 'lib/api-routes';
import { TouchpointType } from 'lib/models/touchpoint';
import { TouchpointState } from 'lib/models/opportunity';
import { useAuth } from 'lib/providers/AuthProvider';
import Step3 from './Step3';
import Step2 from './Step2';
import Step1 from './Step1';
import styles from './Event.module.scss';

export interface EventProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  isSocietyEvent?: boolean;
}

export const Event: React.FC<EventProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    isSocietyEvent,
  }: EventProps) => {
    const contentClassNames = cn(
      {
        [styles.Event]: true,
      },
      className
    );

    const router = useRouter();
    const notificationInstance = useNotification();
    const { step: postingStep, id: touchpointId } = router.query;
    const step = postingStep ? Number(postingStep) : undefined;
    const { isManager } = useAuth();
    const [isLoading, setLoading] = useState(false);
    const [isDraftLoading, setDraftLoading] = useState(false);
    const [touchpoint, setTouchpoint] = useState<EventAttributes | null>(null);

    const {
      removeBanner,
      getEventResponse,
      createEvent,
      updateEvent,
      updateEventStep2,
      updateCompanyEventStep3,
    } = useEvent();

    const { mutateEvents } = useCampaigns();

    useEffect(() => {
      if (!step || ((step === 2 || step === 3) && !touchpointId)) {
        router.replace(`${router.asPath}?step=1`);
      }
    }, [step, touchpointId]);

    const editPathName = isManager
      ? PAGE_ROUTES.EVENTS_EDIT
      : PAGE_ROUTES.CAMPAIGNS_EDIT;

    useEffect(() => {
      const handlePopstate = () => {
        if (
          window.history.state?.edit &&
          window.location.pathname.includes(
            editPathName.replace('[id]', touchpointId as string)
          )
        ) {
          router.replace(
            `${editPathName.replace(
              '[id]',
              touchpointId as string
            )}?step=1&type=${TouchpointType.Event}`
          );
        }
      };
      window.addEventListener('popstate', handlePopstate);
      return () => {
        window.removeEventListener('popstate', handlePopstate);
      };
    }, []);

    useEffect(() => {
      (async () => {
        if (router.pathname === editPathName) {
          const { id } = router.query;
          if (id) {
            const pathname = isManager
              ? `${API_ROUTES.SOCIETY_TOUCHPOINTS}/${id}`
              : `${API_ROUTES.COMPANY_TOUCHPOINTS}/${id}`;
            const state = await getEventResponse(pathname);
            setTouchpoint(state as EventAttributes);
          }
        }
      })();
    }, [router]);

    const { id, title } = touchpoint || {};

    const pathname = useMemo(() => {
      if (router.pathname === editPathName) {
        const { id } = router.query;
        return id ? editPathName.replace('[id]', id as string) : '';
      } else {
        return router.pathname;
      }
    }, [router, id]);

    const onStep1Completion = async (
      data: EventRequestStep1,
      isValueEdited: boolean,
      goToStep: number
    ) => {
      setLoading(true);
      try {
        const continueStep = () => {
          mutateEvents();
          if (data?.submitType === TouchpointState.SAVE) {
            notificationInstance.success({
              title: 'Changes saved!',
              message: `${title} has been saved successfully.`,
            });
            router.push(PAGE_ROUTES.CAMPAIGNS);
          } else {
            router.push(
              `${pathname}?step=${goToStep}&type=${TouchpointType.Event}`
            );
          }
        };

        if (touchpoint && id) {
          if (!isValueEdited) {
            setLoading(false);
            continueStep();
          } else {
            const response = await updateEvent({ touchpoint: data, id });
            if (response) {
              setTouchpoint(response);
              setLoading(false);
              continueStep();
            } else {
              setLoading(false);
            }
          }
        } else {
          const response = await createEvent({ touchpoint: data });
          if (response) {
            const id = response.id;
            setTouchpoint(response);
            setLoading(false);
            if (data?.submitType === TouchpointState.SAVE) {
              notificationInstance.success({
                title: 'Changes saved!',
                message: `${title} has been saved successfully.`,
              });
              mutateEvents();
              router.push(PAGE_ROUTES.CAMPAIGNS);
            } else {
              const path = `${editPathName.replace('[id]', id)}`;
              window.history.replaceState(
                { edit: true },
                '',
                `${path}?step=1&type=${TouchpointType.Event}`
              );
              router.push(
                `${path}?step=${goToStep}&type=${TouchpointType.Event}`
              );
            }
          } else {
            setLoading(false);
          }
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };

    const onStep2Completion = async (
      data: SocietyEventRequestStep2,
      isValueEdited: boolean,
      goToStep: number
    ) => {
      setLoading(true);
      try {
        const continueStep = () => {
          mutateEvents();
          if (data?.submitType === TouchpointState.SAVE) {
            notificationInstance.success({
              title: 'Changes Saved!',
              message: `${title} has been saved as draft successfully.`,
            });
            router.push(PAGE_ROUTES.CAMPAIGNS);
          } else {
            router.push(
              `${pathname}?step=${goToStep}&type=${TouchpointType.Event}`
            );
          }
        };

        if (touchpoint && id) {
          if (!isValueEdited) {
            setLoading(false);
            continueStep();
          } else {
            const response = await updateEventStep2({ touchpoint: data, id });
            if (response) {
              setTouchpoint(response);
              setLoading(false);
              continueStep();
            } else {
              setLoading(false);
            }
          }
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    };

    const onStep3Completion = async ({
      data,
      isValueEdited,
      goToStep,
      isPreviewClicked,
    }: {
      data: CompanyEventRequestStep3;
      isValueEdited: boolean;
      goToStep?: number;
      isPreviewClicked?: boolean;
    }) => {
      if (data?.submitType === TouchpointState.DRAFT) {
        setDraftLoading(true);
      } else {
        setLoading(true);
      }
      try {
        const continueStep = () => {
          if (isPreviewClicked) return;
          mutateEvents();
          if (goToStep) {
            router.push(
              `${pathname}?step=${goToStep}&type=${TouchpointType.Event}`
            );
          } else if (data.state === TouchpointState.PUBLISHED) {
            notificationInstance.success({
              title: 'Event published!',
              message: `${title} has been published successfully.`,
            });
            router.push(`${PAGE_ROUTES.CAMPAIGNS}/${id}`);
          } else if (
            data?.submitType === TouchpointState.SAVE ||
            data?.submitType === TouchpointState.DRAFT
          ) {
            if (data?.submitType === TouchpointState.SAVE) {
              notificationInstance.success({
                title: 'Changes saved!',
                message: `${title} has been saved successfully.`,
              });
            } else if (data?.submitType === TouchpointState.DRAFT) {
              notificationInstance.success({
                title: 'Event saved!',
                message: `${title} has been saved to drafts successfully.`,
              });
            }
            mutateEvents();
            router.push(`${PAGE_ROUTES.CAMPAIGNS}/${id}`);
          } else {
            notificationInstance.success({
              title: `Event ${isSocietyEvent ? 'saved' : 'submitted'}!`,
              message: `${title} has been  ${
                isSocietyEvent ? 'saved' : 'submitted'
              } successfully.`,
            });
            mutateEvents();
            router.push(PAGE_ROUTES.CAMPAIGNS);
          }
        };

        if (touchpoint && id) {
          if (!isValueEdited) {
            setDraftLoading(false);
            setLoading(false);
            continueStep();
          } else {
            const response = await updateCompanyEventStep3({
              touchpoint: data,
              id,
            });

            if (response) {
              setTouchpoint(response);
              setDraftLoading(false);
              setLoading(false);
              if (isPreviewClicked) return;
              else if (goToStep) {
                router.push(
                  `${pathname}?step=${goToStep}&type=${TouchpointType.Event}`
                );
              } else if (
                response.state === TouchpointState.PUBLISHED ||
                response.state === TouchpointState.READY_FOR_APPROVAL
              ) {
                notificationInstance.success({
                  title: `Event ${isSocietyEvent ? 'published' : 'submitted'}!`,
                  message: `${title} has been ${
                    isSocietyEvent ? 'published' : 'submitted'
                  } successfully.`,
                });
                mutateEvents();
                router.push(`${PAGE_ROUTES.CAMPAIGNS}/${id}`);
              } else {
                continueStep();
              }
            } else {
              setDraftLoading(false);
              setLoading(false);
            }
          }
        }
      } catch (error) {
        setDraftLoading(false);
        setLoading(false);
        console.error(error);
      }
    };

    if (router.pathname === editPathName && !touchpoint) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }
    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid={isSocietyEvent ? 'SocietyEvent' : 'CompanyEvent'}
      >
        {Number(step) === 1 ? (
          <Step1
            step={1}
            touchpoint={touchpoint}
            onSave={onStep1Completion}
            removeBanner={removeBanner}
            isLoading={isLoading}
          />
        ) : null}
        {Number(step) === 2 ? (
          <Step2
            step={2}
            touchpoint={touchpoint}
            onSave={onStep2Completion}
            isLoading={isLoading}
          />
        ) : null}
        {Number(step) === 3 ? (
          <Step3
            step={3}
            touchpoint={touchpoint}
            onSave={onStep3Completion}
            isDraftLoading={isDraftLoading}
            isLoading={isLoading}
            isSocietyEvent={isSocietyEvent}
          />
        ) : null}
      </div>
    );
  }
);

Event.displayName = 'Event';
